import React, { useEffect, useState } from 'react'
import SafeIcon from '../../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiX, FiRefreshCw, FiPlus } = FiIcons

const TOTAL_DAYS_PRESETS = [45, 90, 180]
const TOTAL_DAYS_DEFAULT = 180
const TOTAL_DAYS_MIN = 1
const TOTAL_DAYS_MAX = 365

const PREDEFINED_SUBJECTS = [
  'Math',
  'Science',
  'Language Arts',
  'History/Social Studies',
  'Reading/Literature',
  'Physical Education',
  'Visual/Performing Arts',
  'Faith & Growth',
  'Enrichment'
]

const emptyForm = {
  name: '',
  description: '',
  grade_level: '',
  subject: '',
  total_days: TOTAL_DAYS_DEFAULT,
  total_days_custom: '',
  subjects: []
}

const CurriculumFormDialog = ({
  isOpen,
  mode = 'create',
  initialValues = emptyForm,
  onClose,
  onSubmit,
  isSaving = false,
  errorMessage
}) => {
  const [formValues, setFormValues] = useState(emptyForm)
  const [totalDaysMode, setTotalDaysMode] = useState('preset') // 'preset' | 'custom'
  const [customSubjectInput, setCustomSubjectInput] = useState('')

  useEffect(() => {
    if (!isOpen) return
    const normalized = {
      ...emptyForm,
      ...initialValues,
      name: initialValues.name ?? '',
      description: initialValues.description ?? '',
      grade_level: initialValues.grade_level ?? '',
      subject: initialValues.subject ?? '',
      total_days: Number(initialValues.total_days) || TOTAL_DAYS_DEFAULT,
      total_days_custom: initialValues.total_days_custom ?? '',
      subjects: Array.isArray(initialValues.subjects) ? [...initialValues.subjects] : (initialValues.subject ? [initialValues.subject] : [])
    }
    setFormValues(normalized)
    const isCustom = normalized.total_days_custom !== '' ||
      (normalized.total_days && !TOTAL_DAYS_PRESETS.includes(normalized.total_days))
    setTotalDaysMode(isCustom ? 'custom' : 'preset')
    if (isCustom && normalized.total_days) setFormValues(prev => ({ ...prev, total_days_custom: String(normalized.total_days) }))
    setCustomSubjectInput('')
  }, [initialValues, isOpen])

  if (!isOpen) return null

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormValues(prev => ({ ...prev, [name]: value }))
  }

  const handleTotalDaysPreset = (num) => {
    setTotalDaysMode('preset')
    setFormValues(prev => ({ ...prev, total_days: num, total_days_custom: '' }))
  }

  const handleTotalDaysCustom = (value) => {
    setTotalDaysMode('custom')
    const cleaned = value.replace(/\D/g, '')
    setFormValues(prev => ({ ...prev, total_days_custom: cleaned, total_days: cleaned ? Math.min(TOTAL_DAYS_MAX, Math.max(TOTAL_DAYS_MIN, parseInt(cleaned, 10))) : TOTAL_DAYS_DEFAULT }))
  }

  const toggleSubject = (subject) => {
    setFormValues(prev => ({
      ...prev,
      subjects: prev.subjects.includes(subject)
        ? prev.subjects.filter(s => s !== subject)
        : [...prev.subjects, subject]
    }))
  }

  const addCustomSubject = () => {
    const trimmed = customSubjectInput.trim()
    if (!trimmed || formValues.subjects.includes(trimmed)) return
    setFormValues(prev => ({ ...prev, subjects: [...prev.subjects, trimmed] }))
    setCustomSubjectInput('')
  }

  const removeSubject = (subject) => {
    setFormValues(prev => ({ ...prev, subjects: prev.subjects.filter(s => s !== subject) }))
  }

  const getEffectiveTotalDays = () => {
    if (totalDaysMode === 'custom' && formValues.total_days_custom) {
      const n = parseInt(formValues.total_days_custom, 10)
      return Math.min(TOTAL_DAYS_MAX, Math.max(TOTAL_DAYS_MIN, isNaN(n) ? TOTAL_DAYS_DEFAULT : n))
    }
    return Math.min(TOTAL_DAYS_MAX, Math.max(TOTAL_DAYS_MIN, formValues.total_days || TOTAL_DAYS_DEFAULT))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    const totalDays = getEffectiveTotalDays()
    if (totalDays < TOTAL_DAYS_MIN || totalDays > TOTAL_DAYS_MAX) return
    onSubmit({
      ...formValues,
      total_days: totalDays,
      subjects: formValues.subjects.filter(Boolean),
      subject: formValues.subject?.trim() || (formValues.subjects[0] ?? '')
    })
  }

  const title = mode === 'edit' ? 'Edit Curriculum' : 'Create Curriculum'
  const actionLabel = mode === 'edit' ? 'Save Changes' : 'Create Curriculum'
  const effectiveTotalDays = getEffectiveTotalDays()
  const totalDaysError = effectiveTotalDays < TOTAL_DAYS_MIN || effectiveTotalDays > TOTAL_DAYS_MAX

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl border border-gray-200 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <div>
            <h3 className="text-xl font-semibold text-gray-800">{title}</h3>
            <p className="text-sm text-gray-500">Capture the basics for this curriculum</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            aria-label="Close"
          >
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
            <input
              type="text"
              name="name"
              value={formValues.name}
              onChange={handleChange}
              required
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#00AAFF]"
              placeholder="e.g., 6th Grade Complete Curriculum"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              name="description"
              value={formValues.description}
              onChange={handleChange}
              required
              rows="3"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#00AAFF]"
              placeholder="What is the focus and goal for this curriculum?"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Grade Level</label>
              <input
                type="text"
                name="grade_level"
                value={formValues.grade_level}
                onChange={handleChange}
                required
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#00AAFF]"
                placeholder="e.g., 6th Grade"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Primary subject (optional)</label>
              <input
                type="text"
                name="subject"
                value={formValues.subject}
                onChange={handleChange}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#00AAFF]"
                placeholder="e.g., Science"
              />
            </div>
          </div>

          {/* Total days */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Total school days</label>
            <div className="flex flex-wrap gap-3 mb-2">
              {TOTAL_DAYS_PRESETS.map((num) => (
                <label key={num} className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="total_days_preset"
                    checked={totalDaysMode === 'preset' && formValues.total_days === num}
                    onChange={() => handleTotalDaysPreset(num)}
                    className="text-[#00AAFF] focus:ring-[#00AAFF]"
                  />
                  <span className="text-gray-700">{num} days</span>
                </label>
              ))}
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="total_days_preset"
                  checked={totalDaysMode === 'custom'}
                  onChange={() => setTotalDaysMode('custom')}
                  className="text-[#00AAFF] focus:ring-[#00AAFF]"
                />
                <span className="text-gray-700">Custom</span>
              </label>
            </div>
            {totalDaysMode === 'custom' && (
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={TOTAL_DAYS_MIN}
                  max={TOTAL_DAYS_MAX}
                  value={formValues.total_days_custom}
                  onChange={(e) => handleTotalDaysCustom(e.target.value)}
                  placeholder="1–365"
                  className="w-28 rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#00AAFF]"
                />
                <span className="text-sm text-gray-500">days (1–365)</span>
              </div>
            )}
            {totalDaysError && (
              <p className="text-sm text-red-600 mt-1">Total days must be between {TOTAL_DAYS_MIN} and {TOTAL_DAYS_MAX}.</p>
            )}
          </div>

          {/* Subjects multi-select */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Subjects</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {PREDEFINED_SUBJECTS.map((subj) => (
                <button
                  key={subj}
                  type="button"
                  onClick={() => toggleSubject(subj)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    formValues.subjects.includes(subj)
                      ? 'bg-[#00AAFF] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {subj}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 mb-2">
              {formValues.subjects.filter(s => !PREDEFINED_SUBJECTS.includes(s)).map((subj) => (
                <span
                  key={subj}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm bg-[#00AAFF]/20 text-[#1B4C87]"
                >
                  {subj}
                  <button type="button" onClick={() => removeSubject(subj)} className="hover:text-red-600" aria-label={`Remove ${subj}`}>
                    ×
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={customSubjectInput}
                onChange={(e) => setCustomSubjectInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomSubject())}
                placeholder="Add custom subject"
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#00AAFF]"
              />
              <button
                type="button"
                onClick={addCustomSubject}
                className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 text-sm"
              >
                <SafeIcon icon={FiPlus} className="w-4 h-4" />
                Add
              </button>
            </div>
          </div>

          {errorMessage && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-100 px-3 py-2 rounded-lg">
              <span className="font-semibold">Error:</span>
              <span>{errorMessage}</span>
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving || totalDaysError}
              className="px-5 py-2 rounded-lg bg-[#00AAFF] text-white hover:bg-[#0099E5] transition-colors disabled:opacity-60 flex items-center gap-2"
            >
              {isSaving && <SafeIcon icon={FiRefreshCw} className="w-4 h-4 animate-spin" />}
              {actionLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CurriculumFormDialog
